import React, { useEffect, useState } from 'react';
import useUiStore from '../../store/uiStore';
import type { Toast as ToastType } from '../../store/uiStore';

/**
 * Renders toasts from `uiStore` and auto-dismisses them after `durationMs`.
 */
export default function Toasts(): React.ReactElement | null {
  const toasts = useUiStore((s) => s.toasts);

  if (!toasts || toasts.length === 0) return null;

  return (
    <div
      aria-live="polite"
      style={{
        position: 'sticky',
        bottom: 0,
        display: 'flex',
        flexDirection: 'column-reverse',
        gap: 8,
        alignItems: 'center',
        pointerEvents: 'none',
        zIndex: 1000,
        padding: '8px',
      }}
    >
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} />
      ))}
    </div>
  );
}

function ToastItem({ toast }: { toast: ToastType }) {
  const dismiss = useUiStore((s) => s.dismissToast);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // animate in
    const enter = setTimeout(() => setVisible(true), 10);
    const timeout = setTimeout(() => dismiss(toast.id), toast.durationMs ?? 4000);
    return () => {
      clearTimeout(enter);
      clearTimeout(timeout);
    };
  }, [dismiss, toast.id, toast.durationMs]);

  const bgColor = toast.level === 'success' ? '#107c10' : toast.level === 'error' ? '#d13438' : '#005a9e';

  const roleAttr = toast.level === 'error' ? 'alert' : 'status';
  const ariaLive = toast.level === 'error' ? 'assertive' : 'polite';

  return (
    <div
      role={roleAttr}
      aria-live={ariaLive}
      aria-atomic="true"
      style={{
        pointerEvents: 'auto',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(8px)',
        transition: 'opacity 200ms ease, transform 200ms ease',
        background: bgColor,
        color: 'white',
        padding: '10px 14px',
        borderRadius: 6,
        minWidth: 240,
        boxShadow: '0 6px 18px rgba(0,0,0,0.12)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <div style={{ flex: 1, marginRight: 12 }}>{toast.message}</div>
      <button
        onClick={() => dismiss(toast.id)}
        aria-label="Dismiss"
        style={{
          background: 'transparent',
          border: 'none',
          color: 'rgba(255,255,255,0.9)',
          cursor: 'pointer',
          fontSize: 14,
        }}
      >
        ×
      </button>
    </div>
  );
}
